import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FlatButton,
  FormProvider,
  HealthPlanStatuses,
  List,
  PayoutRuleType,
  PlusIcon,
  ServiceConfig,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import theme from '@/styles/theme';

import AddServicePaymentModal from '../../AddServicePaymentModal';
import useLinkedPlanOptions from '../../ProcessingRuleModalV2/hooks/useLinkedPlanOptions';
import { useProcessingRuleTiersStore } from '../../stores/useProcessingRuleTiersStore';
import { TierField } from '../hooks/useTiers';
import ServiceConfigItem from '../ServiceConfig/ServiceConfigItem';

import { useTierForm } from './hooks';
import TierFormFields from './TierFormFields';
import { CARD_PURSE_LOCK, TierFormField, TierUpdateComputation } from './tierItem.types';

const StyledButton = styled(FlatButton)`
  &:hover {
    opacity: 0.8;
  }
`;

interface Props {
  isRemoveMode?: boolean;
  isLastItem?: boolean;
  id: string;
  index: number;
  defaultValues?: Partial<TierField>;
  isErrorShowed?: boolean;
  isTemplateMode?: boolean;
  isEditMode?: boolean;
  payoutDefinitionId: string;
  serviceConfigs?: ServiceConfig[];
  deductibleType?: string;
  healthPlanTypes?: string[];
  healthPlanStatus?: HealthPlanStatuses;
  isEmbeddedOnly?: boolean;
}

const TierItem: React.FC<Props> = ({
  id,
  isErrorShowed,
  index,
  defaultValues,
  serviceConfigs,
  healthPlanStatus,
  isLastItem,
  isTemplateMode,
  isEditMode,
  payoutDefinitionId,
  deductibleType,
  healthPlanTypes,
  isEmbeddedOnly,
}) => {
  const [updatedElement, setUpdatedElement] = useState<ServiceConfig>();
  const [visible, setVisible] = useState(false);
  const handleToggleVision = useCallback(() => {
    setVisible(!visible);
  }, [visible]);

  useEffect(() => {
    if (updatedElement) setVisible(true);
  }, [updatedElement]);

  const { methods } = useTierForm(defaultValues);

  const isPurseLockUpdateEnabled = useFeatureFlag(FeatureFlags.PURSE_LOCK_HP_UPDATE);
  const linkedPlanOptions = useLinkedPlanOptions();

  const setTier = useProcessingRuleTiersStore((state) => state.setTier);
  const payoutRuleType = methods.watch(TierFormField.payoutRuleType);
  const lockCardPurse = methods.watch(TierFormField.lockCardPurse);
  const planIdsForCardPurseLock = methods.watch(TierFormField.planIdsForCardPurseLock);
  const isNoExceptions = payoutRuleType === PayoutRuleType.NO_EXCEPTIONS;
  const isLockCardPurseAvailable = isPurseLockUpdateEnabled && !isEmbeddedOnly && lockCardPurse !== undefined;

  const { tierUpdate, linkedPlanIds } = useMemo<TierUpdateComputation>(() => {
    if (!isLockCardPurseAvailable || !payoutRuleType) {
      return { tierUpdate: payoutRuleType ? { tierId: id, payoutRuleType } : undefined, linkedPlanIds: [] };
    }

    const isLocked = lockCardPurse === CARD_PURSE_LOCK;
    const planIds = linkedPlanOptions.map((option) => `${option.key}`);
    const selectedPlanIds =
      isLocked && !isTemplateMode
        ? (planIdsForCardPurseLock || []).filter((planId) => planIds.includes(planId))
        : [];

    return {
      tierUpdate: { tierId: id, payoutRuleType, isLockPurse: isLocked, planIds: selectedPlanIds },
      linkedPlanIds: planIds,
    };
  }, [
    id,
    isLockCardPurseAvailable,
    isTemplateMode,
    payoutRuleType,
    lockCardPurse,
    planIdsForCardPurseLock,
    linkedPlanOptions,
  ]);

  useEffect(() => {
    if (!tierUpdate) return;

    // Drop plans that were unlinked from the health plan so the form stays in sync.
    if (
      isLockCardPurseAvailable &&
      !isTemplateMode &&
      (planIdsForCardPurseLock || []).some((planId) => !linkedPlanIds.includes(planId))
    ) {
      methods.setValue(TierFormField.planIdsForCardPurseLock, tierUpdate.planIds ?? []);
    }

    setTier(tierUpdate);
  }, [
    tierUpdate,
    isLockCardPurseAvailable,
    isTemplateMode,
    planIdsForCardPurseLock,
    linkedPlanIds,
    methods,
    setTier,
  ]);

  return (
    <Box key={id} pad="m" background="module" round="moduleRound" margin={{ bottom: isLastItem ? '0' : 'l' }}>
      <Box
        round="container1Round"
        background="canvas"
        pad={{ vertical: 'spacing24', horizontal: 'spacing32' }}
        border={{ side: 'all', size: 'small', color: theme.colors.border2 }}
      >
        <Box direction="row" align="center" justify="between">
          <Text size="xxlarge" weight={400}>{`Tier ${index + 1}`}</Text>
          {!isTemplateMode && !isNoExceptions && (
            <StyledButton onClick={handleToggleVision}>
              <Box direction="row" align="center" justify="center">
                <Text margin={{ right: 's' }} color="buttonPrimary" weight={400}>
                  Add Service Payment
                </Text>
                <PlusIcon size="small" color="buttonPrimary" />
              </Box>
            </StyledButton>
          )}
          {!isTemplateMode && !isNoExceptions && visible && (
            <AddServicePaymentModal
              visible={visible}
              setVisible={handleToggleVision}
              tierId={id}
              payoutDefinitionId={payoutDefinitionId}
              updatedElement={updatedElement}
              setUpdatedElement={setUpdatedElement}
              deductibleType={deductibleType}
              healthPlanTypes={healthPlanTypes}
              serviceConfigs={serviceConfigs}
            />
          )}
        </Box>
        <FormProvider {...methods}>
          <TierFormFields
            id={id}
            viewMode
            isPayoutRuleEditable={isEditMode}
            isTemplateMode={isTemplateMode}
            isEmbeddedOnly={isEmbeddedOnly}
            isLockCardPurseAvailable={isLockCardPurseAvailable}
            isLockCardPurseEditable={isEditMode}
            linkedPlanOptions={linkedPlanOptions}
          />
        </FormProvider>
      </Box>
      {!isNoExceptions && !!serviceConfigs?.length && (
        <List.Header style={{ marginTop: theme.spacings.m }}>
          <List.Header.Item flex={1.5}>Service(s)</List.Header.Item>
          <List.Header.Item flex={1.5}>Claim amount</List.Header.Item>
          <List.Header.Item flex={1}>Applied to deductible</List.Header.Item>
          <Box width="24px" />
        </List.Header>
      )}
      {!isNoExceptions &&
        serviceConfigs?.map((item) => (
          <ServiceConfigItem
            key={item?.id}
            value={item}
            setUpdatedElement={setUpdatedElement}
            healthPlanStatus={healthPlanStatus}
          />
        ))}
    </Box>
  );
};

export default TierItem;
